// Lógica pura derivada del progreso · sin side-effects · 100% testeable sin BD.
// Owner: Carlos Beltrán · sprint Campus Wave 1 A1 · 2026-05-25.
//
// Diseñado para trabajar con `UserProgress` ya cargado vía
// `fetchProgress()`. NO hace fetch ni dispatch · solo deriva info visual.

import type { ModuleProgress, ModuleStatus, UserProgress } from './types';

export type ProgressColor = 'green' | 'amber' | 'gray';

export interface GlobalProgress {
  completed: number;
  total: number;
  percent: number;
  color: ProgressColor;
}

export type NextStepKind = 'start' | 'continue' | 'done';

export interface NextStep {
  kind: NextStepKind;
  module: ModuleProgress | null;
}

/**
 * Devuelve la racha (días naturales consecutivos) de actividad del usuario,
 * basada en `last_seen_at` aproximado. Por ahora usamos `updated_at` del
 * progreso global como proxy hasta que Andrés añada un campo dedicado en BD
 * (`campus.user_progress.streak_days` / `last_activity_date`).
 *
 * Reglas:
 * - Si `lastActivity` es null o > 1 día respecto a `today` (UTC), streak = 0.
 * - Si hubo actividad hoy o ayer (en UTC) devolvemos el `cachedStreak` si lo hay.
 * - Cuando no haya cache, devolvemos 1 (al menos hubo actividad reciente).
 *
 * Esto es un mock determinista hasta que el backend persista la racha real.
 * Una vez disponible: pasar `cachedStreak` desde `UserProgress.streak_days`.
 */
export function calculateStreak(
  lastActivityISO: string | null | undefined,
  today: Date = new Date(),
  cachedStreak: number | null = null,
): number {
  if (!lastActivityISO) return 0;
  const last = new Date(lastActivityISO);
  if (Number.isNaN(last.getTime())) return 0;

  const dayMs = 24 * 60 * 60 * 1000;
  const lastUTC = Date.UTC(
    last.getUTCFullYear(),
    last.getUTCMonth(),
    last.getUTCDate(),
  );
  const todayUTC = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const diffDays = Math.round((todayUTC - lastUTC) / dayMs);

  if (diffDays < 0 || diffDays > 1) return 0;

  if (cachedStreak !== null && cachedStreak > 0) {
    return cachedStreak;
  }
  return 1;
}

/**
 * Suma de módulos en estado `completed` sobre el total.
 * Color por umbrales (idéntico spec A1):
 *   0-39  → gray
 *   40-79 → amber
 *   80+   → green
 */
export function calculateGlobalProgress(
  modules: ModuleProgress[],
): GlobalProgress {
  const total = modules.length;
  const completed = modules.filter((m) => m.status === 'completed').length;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  let color: ProgressColor = 'gray';
  if (percent >= 80) color = 'green';
  else if (percent >= 40) color = 'amber';
  return { completed, total, percent, color };
}

/**
 * Primer módulo accionable que el usuario debería hacer ahora.
 * Orden de prioridad:
 *   1. Si hay módulos `in_progress` → el primero (continuar lo abierto).
 *   2. Si no, primer módulo `locked|in_progress` no completado en orden natural.
 *   3. Si todos completados → kind='done'.
 *   4. Si lista vacía → kind='start', module=null.
 *
 * El orden natural se asume el del array (backend devuelve por `number` asc
 * dentro de cada fase y demo antes que real, ver serializers.py).
 */
export function getNextStep(modules: ModuleProgress[]): NextStep {
  if (modules.length === 0) {
    return { kind: 'start', module: null };
  }

  const inProgress = modules.find((m) => m.status === 'in_progress');
  if (inProgress) {
    return { kind: 'continue', module: inProgress };
  }

  const firstPending = modules.find(
    (m: ModuleProgress) => (m.status as ModuleStatus) !== 'completed',
  );
  if (firstPending) {
    // Cualquier no-completado se trata como "continuar" (puede estar lock pero
    // es siguiente paso natural a desbloquear).
    return { kind: 'continue', module: firstPending };
  }

  return { kind: 'done', module: null };
}

/** Helper: detecta si el usuario aún no ha tocado nada (overall_status). */
export function isFreshStart(progress: UserProgress): boolean {
  return progress.overall_status === 'not_started';
}
